import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { FloatingCtaProvider, useFloatingCtaState } from './floatingCtaContext';
import { FEATURE_MENU_GROUPS, getFeatures } from '../lib/features';
import { FeatureIcon } from './FeatureIcon';
// Map top-level nav labels to their live routes.
// NOTE: Enterprise, Security and Resources/Blog were moved to the noindex
// /unlisted namespace, so they're intentionally NOT linked from the nav.
const NAV_ROUTES: Record<string, string> = {
  Pricing: '/pricing',
  ChatGPT: '/chatgpt',
  Integrations: '/integrations',
  'Become an Affiliate': 'https://partners.dub.co/getgiga/affiliate-program'
};
// Nav labels whose target is an EXTERNAL site — rendered as a real anchor that
// opens in a new tab rather than an internal router Link.
const EXTERNAL_NAV = new Set(['Become an Affiliate']);
// Labels shown in the home-page top nav.
const NAV_LABELS = ['Features', 'Integrations', 'Pricing', 'ChatGPT'];
export function Layout() {
  return <FloatingCtaProvider data-id="element-3716" data-mp="1rkqtoexg1bhm">
      <LayoutInner data-id="element-3717" data-mp="1rlidytllbmsl" />
    </FloatingCtaProvider>;
}
function LayoutInner() {
  // While a section (e.g. the integration "plugs into your stack" beat) owns
  // its own primary CTA, it suppresses this global floating "Try Giga for free"
  // button so the two don't compete. It fades back in once that beat passes.
  const floatingCtaSuppressed = useFloatingCtaState();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  // True when the floating mobile "Try Giga for free" CTA is sitting over an
  // ORANGE surface (the founder note, the footer, etc.) — the CTA is orange
  // too, so it would vanish against those. When that happens we flip the
  // button to a white treatment so it stays visible. Any orange section can
  // opt in by tagging its element with data-cta-invert; the footer is tagged
  // too.
  const [overOrange, setOverOrange] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [featureMenuDismissed, setFeatureMenuDismissed] = useState(false);
  const features = getFeatures();
  const featureGroups = FEATURE_MENU_GROUPS.map(group => ({
    ...group,
    items: group.slugs.map(slug => features.find(feature => feature.slug === slug)).filter(Boolean)
  }));
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510 / #1A1410) hero, so while the header
  // is still transparent (before the user scrolls) the nav must switch to a
  // light treatment — white links and a white logo — to stay legible. Once
  // scrolled, the header turns solid white and reverts to the dark-on-light
  // treatment.
  const darkHeroRoutes = ['/unlisted/compare/chatgpt', '/unlisted/security', '/demo', '/unlisted/team-workspace'];
  const isTeamWorkspace = location.pathname === '/unlisted/team-workspace';
  const isFeatureRoute = location.pathname.startsWith('/features/');
  const onDark = (darkHeroRoutes.includes(location.pathname) || isFeatureRoute) && !isScrolled;
  // Both full-screen home heroes own their primary action at the top of the
  // page, so the header uses a clear white Log in action until scrolling begins.
  const atHeroTop = (location.pathname === '/ai-coworker' || isTeamWorkspace) && !isScrolled;
  const navLinkClass = `text-sm font-semibold transition-colors ${onDark ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  // /alt owns a sticky pillar nav of its own, and two stacked bars competing
  // for the top of the viewport is one too many — so there the site header
  // scrolls away with the page and the pillar nav takes the top edge.
  const headerScrollsAway = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Watch every orange surface (the footer + any section tagged with
  // data-cta-invert, e.g. the founder note): when one of them is under the
  // bottom band of the viewport — exactly where the floating mobile CTA sits —
  // flip the CTA to its white treatment so the orange button doesn't blend
  // into the orange background. The negative top rootMargin shrinks the
  // observed region to just the bottom ~12% strip, so the flip happens only
  // while the CTA truly overlaps the orange surface (not the moment any part
  // of a tall orange section first peeks into view). Re-runs on route change
  // so it picks up the orange sections present on the current page.
  useEffect(() => {
    const surfaces = new Set<Element>();
    document.querySelectorAll('[data-cta-invert]').forEach(el => surfaces.add(el));
    if (footerRef.current) surfaces.add(footerRef.current);
    if (surfaces.size === 0) {
      setOverOrange(false);
      return;
    }
    const active = new Set<Element>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) active.add(entry.target);else active.delete(entry.target);
      });
      setOverOrange(active.size > 0);
    }, {
      // Only the bottom strip of the viewport counts — that's where the
      // floating CTA lives.
      rootMargin: '-88% 0px 0px 0px',
      threshold: 0
    });
    surfaces.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-3718" data-mp="fyrq1414tne6">
      <header className={`${headerScrollsAway ? 'absolute' : 'fixed'} top-0 w-full z-50 translate-y-0 transition-[background-color,padding,box-shadow] duration-300 ${isScrolled && !headerScrollsAway ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3719" data-mp="tq67v7jazijn">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-3720" data-mp="yny1c5zljcpy">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-3721" data-mp="1icdozj5zt5ex">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-3722" data-mp="1n9m6dhjbh5f4" />

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-3723" data-mp="zj9tds97siho" />
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-id="element-3724" data-mp="uk5hgtuwo6nn">
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="relative group py-2" onMouseLeave={() => setFeatureMenuDismissed(false)} data-id="element-3725" data-mp="ko2k01b00w71">
                  <button type="button" className={`${navLinkClass} inline-flex items-center gap-1`} aria-haspopup="menu" onFocus={() => setFeatureMenuDismissed(false)} data-id="element-3726" data-mp="1144oq1puqxt3">Features <ChevronDown size={14} data-id="element-3727" data-mp="w2fbcff5q81u" /></button>
                  <div className={`invisible absolute left-1/2 top-full w-[760px] -translate-x-1/2 translate-y-2 rounded-2xl border border-neutral-200 bg-white p-4 opacity-0 shadow-2xl transition-all duration-150 ${featureMenuDismissed ? '' : 'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100'}`} data-id="element-3728" data-mp="r4m7vu58ns59">
                    <div className="grid grid-cols-3 gap-4" role="menu" data-id="element-3729" data-mp="8fz1977eyupk">
                      {featureGroups.map(group => <div key={group.key} data-id="element-3730" data-mp="3gvzpwqn76mc">
                        <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400" data-id="element-3731" data-mp="3dzzu2v8i7v9">{group.label}</p>
                        <div className="space-y-0.5" data-id="element-3732" data-mp="3qk0r0b9bzy2g">
                          {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} role="menuitem" onClick={() => setFeatureMenuDismissed(true)} className="flex items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-[#FFF4EC] transition-colors" data-id="element-3733" data-mp="26318i9lziznn">
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3734" data-mp="2f3zid8j3u1e5"><FeatureIcon name={feature.icon} size={16} data-id="element-3735" data-mp="2k06ksrcakurc" /></span>
                            <span className="min-w-0 text-sm font-semibold leading-tight text-[#2B211A]" data-id="element-3736" data-mp="1mg3i5tubtrcm">{feature.menuLabel || feature.name}</span>
                          </Link>)}
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className={navLinkClass} data-id="element-3737" data-mp="215hsb45q8ct2">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className={`${navLinkClass} inline-flex items-center gap-1.5`} data-id="element-3738" data-mp="18m2kz4j0wced">
                  {label}{label === 'ChatGPT' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3739" data-mp="nsqhy3v2nvj3">New</span>}
                </Link>)}
          </nav>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-3740" data-mp="spz38dbiofok">
            {atHeroTop ?
          // Hero already shows the primary "Try Giga for free" CTA — keep a
          // quiet white Sign in button here until the user scrolls past it.
          // While Giga is gated, Login opens the waitlist too (the /login
          // page is preserved for later — see App.tsx route).
          <WaitlistCTA analyticsLabel="nav-sign-in" spinnerClassName="text-[#2B211A]" className="hidden md:block bg-white hover:bg-[#FFF4EC] text-[#2B211A] border border-white/80 px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-3741" data-mp="a00tztvzggjc">
                {isTeamWorkspace ? 'Log in' : 'Sign in'}
              </WaitlistCTA> : <>
                {/* Login is waitlisted for now �� opens the waitlist instead of
                 the (preserved) /login page. */}
                <WaitlistCTA analyticsLabel="nav-log-in" spinnerClassName="text-[#2B211A]" className={`hidden md:block ${navLinkClass}`} data-id="element-3742" data-mp="1vlqsj7krolne">
                  Log in
                </WaitlistCTA>
                <WaitlistCTA analyticsLabel="nav-try-giga" className="hidden md:block bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-3743" data-mp="1ll6ojx5v531h">
                  Try Giga for free
                </WaitlistCTA>
              </>}

            {/* Mobile control: hamburger, plus a compact "Try for free" CTA.
             The header is only revealed while scrolling UP (it slides away on
             scroll-down), so showing the CTA here gives the user a quick
             conversion tap right next to the menu when they scroll back up.
             Hidden at the very top of the hero, where the hero already shows
             its own primary CTA. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-3744" data-mp="j96y15k2c9u5">
              {isScrolled && <WaitlistCTA analyticsLabel="mobile-try-free" className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors shadow-sm whitespace-nowrap" data-id="element-3745" data-mp="o9bc6oxi0sh7">
                  Try for free
                </WaitlistCTA>}
              <button className={`p-2 -mr-2 transition-colors ${onDark ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-3746" data-mp="120g5l9a9woj3">
                {mobileMenuOpen ? <X size={24} data-id="element-3747" data-mp="t7377rtfwupm" /> : <Menu size={24} data-id="element-3748" data-mp="t7mmf0rph4zy" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Compact */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg pb-3 px-5 flex flex-col gap-1.5 z-50 max-h-[80vh] overflow-y-auto" data-id="element-3749" data-mp="1kz26an1vodmh">
            <div className="sticky top-0 -mx-5 px-5 pt-3 pb-3 bg-white border-b border-neutral-100 flex gap-3 z-10" data-id="element-3750" data-mp="3ag62k6yqzsnt">
              {/* Login is waitlisted for now — opens the waitlist instead of
               the (preserved) /login page. */}
              <WaitlistCTA analyticsLabel="mobile-menu-log-in" spinnerClassName="text-[#2B211A]" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-3751" data-mp="3k5pqhr1bicpj">
                Log in
              </WaitlistCTA>
              <WaitlistCTA analyticsLabel="mobile-menu-try-giga" className="flex-1 text-center bg-[#EA580C] text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-3752" data-mp="2mjjy8am43i6m">
                Try Giga for free
              </WaitlistCTA>
            </div>
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="border-b border-neutral-100" data-id="element-3753" data-mp="gohbrg1vzsz1">
                <button type="button" onClick={() => toggleSection('features')} className="w-full py-2 flex items-center justify-between text-base font-medium text-[#2B211A]" aria-expanded={openSection === 'features'} data-id="element-3754" data-mp="bqyxrb90ink6">
                  Features <ChevronDown size={17} className={`transition-transform ${openSection === 'features' ? 'rotate-180' : ''}`} data-id="element-3755" data-mp="2melzixtvgoxi" />
                </button>
                {openSection === 'features' && <div className="space-y-3 pb-3" data-id="element-3756" data-mp="2ver655r8rjam">
                  {featureGroups.map(group => <div key={group.key} data-id="element-3757" data-mp="30du0nw9li74c">
                    <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-3758" data-mp="30csr5w889soq">{group.label}</p>
                    <div className="grid grid-cols-2 gap-1" data-id="element-3759" data-mp="35akese1uasn4">
                      {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-[#655B54] hover:bg-[#FFF4EC] hover:text-[#2B211A]" data-id="element-3760" data-mp="3a5hxkcox8gd7">
                        <FeatureIcon name={feature.icon} size={15} className="shrink-0 text-[#EA580C]" data-id="element-3761" data-mp="3o3692tx3mb5l" /> <span data-id="element-3762" data-mp="3o128akuhxs9j">{feature.menuLabel || feature.name}</span>
                      </Link>)}
                    </div>
                  </div>)}
                </div>}
              </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2" data-id="element-3763" data-mp="3tad28fekpup9">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2 flex items-center gap-2" data-id="element-3764" data-mp="krxb1s221nlr">
                  {label}{label === 'ChatGPT' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3765" data-mp="b1vrndknctd6">New</span>}
                </Link>)}
          </div>}
      </header>

      <main className="flex-1" data-id="element-3766" data-mp="13iy8gbnj1b37">
        <Outlet data-id="element-3767" data-mp="13jzmmozuadp5" />
      </main>

      {/* Global floating "Try Giga for free" CTA — mobile only. Gives the user
                                    a persistent conversion tap as they scroll, even while the header is slid
                                    away on scroll-down. Hidden at the very top of the hero (where the hero
                                    owns its own primary CTA) and while an in-page section suppresses it.
                                    Over the orange footer it switches to a white treatment so it stays
                                    visible. */}
      <AnimatePresence data-id="element-3768" data-mp="2a7r98ms2nugg">
        {isScrolled && !atHeroTop && !floatingCtaSuppressed ? <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="md:hidden fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 pointer-events-none" data-id="element-3769" data-mp="2f9gmm92rok7p">
            <WaitlistCTA analyticsLabel="floating-cta" spinnerClassName={overOrange ? 'text-[#EA580C]' : 'text-white'} className={['pointer-events-auto inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-bold shadow-lg transition-colors', overOrange ? 'bg-white text-[#EA580C] shadow-black/20' : 'bg-[#EA580C] text-white shadow-[#EA580C]/30'].join(' ')} data-id="element-3770" data-mp="3j4hmpu3np09">
              Try Giga for free
            </WaitlistCTA>
          </motion.div> : null}
      </AnimatePresence>

      <footer ref={footerRef} data-cta-invert="true" className="relative overflow-hidden bg-[#2B211A] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-3771" data-mp="26qn3ml3qa8rr">
        {/* Subtle grid texture */}
        <div className="dot-matrix opacity-20" aria-hidden="true" data-id="element-3772" data-mp="2gpc9tgm9i4s6" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-wordmark.svg" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-3773" data-mp="mcmfwyvw8vks" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3774" data-mp="5945phhf0hub">
          {/* Sitemap. The three feature groups used to stack inside a single
           "Features" column, which ran ~17 links deep and left the other
           three columns empty for most of the footer's height. Each group is
           now its own column, so the row reads as five balanced lists. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-3775" data-mp="3dqqwdllkivo4">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1" data-id="element-3776" data-mp="3no53uvpika4a">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-7 object-contain mb-4 [filter:brightness(0)_invert(1)]" data-id="element-3777" data-mp="xyb50iop1bgm" />

              <p className="text-white/70 leading-relaxed max-w-xs" data-id="element-3778" data-mp="2ne3ya4zxzmrw">
                Everyone's new favorite colleague.
              </p>
            </div>

            {/* One column per feature group, so no single list runs long. */}
            {featureGroups.map(group => <div key={group.key} data-id="element-3779" data-mp="28qsnw6fhy8td">
                <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-3780" data-mp="28pr1x6wy01wv">
                  {group.label}
                </h3>
                <ul className="space-y-2.5" data-id="element-3781" data-mp="1zot3r596qom7">
                  {group.items.map(feature => feature && <li key={feature.slug} data-id="element-3782" data-mp="1upzq9ctwexm1">
                      <Link to={`/features/${feature.slug}`} className="group/footer inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-id="element-3783" data-mp="1uqrnst7235ey">
                        <FeatureIcon name={feature.icon} size={14} className="shrink-0 text-white/55 transition-colors group-hover/footer:text-white" data-id="element-3784" data-mp="3p4iubju39966" />
                        <span data-id="element-3785" data-mp="3k3383og6cwm7">{feature.menuLabel || feature.name}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>)}

            {/* Product and Legal share the last column — together they are
             about the same depth as one feature group. */}
            <div className="space-y-8" data-id="element-3786" data-mp="61ymiln3q237">
              {[{
              title: 'Product',
              links: [{
                label: 'Pricing',
                to: '/pricing'
              }, {
                label: 'Developers',
                to: '/developers'
              }]
            }, {
              title: 'Legal',
              links: [{
                label: 'Support',
                to: '/support'
              }, {
                label: 'Privacy',
                to: '/privacy'
              }, {
                label: 'Terms',
                to: '/terms'
              }, {
                label: 'Vulnerability Disclosure',
                to: '/vulnerability-disclosure'
              }]
            }].map(col => <div key={col.title} data-id="element-3787" data-mp="1l1e3epzt7gxm">
                  <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-3788" data-mp="1l0clc1kdlu6e">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5" data-id="element-3789" data-mp="1ttpmtrwia443">
                    {col.links.map(link => <li key={link.label} data-id="element-3790" data-mp="1tvsdme5hg19v">
                        <Link to={link.to} className="text-white/80 transition-colors hover:text-white" data-id="element-3791" data-mp="1ysskuse2ja2k">
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-3792" data-mp="1b1ww644wsuzj">
            <div className="text-white/60" data-id="element-3793" data-mp="1g2b3tl2lm2x1">
              © {new Date().getFullYear()} Tonic Labs Limited. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-3794" data-mp="2mk7pwsx8c5hi">
              {[{
              label: 'Twitter',
              href: 'https://x.com/askgiga'
            }].map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-3795" data-mp="21s9gbo7hm0lo">
                  {social.label}
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}